import React, { useContext, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Checkbox } from '@fs/zion-ui'
import { DmcGrid } from '../../common/DmcGrid'
import { QueuesContext } from '../QueuesContext'
import { deleteMessageById } from '../../../services/dmqueuesService'
import useNotifications from '../../../hooks/useNotifications'

export default function TableOperations({ queueState, queueActions, message }) {
  const { queuesData } = useContext(QueuesContext)
  const { t } = useTranslation()
  const { error } = useNotifications()
  const [isPending, setIsPending] = useState(false)
  const isMounted = useRef(true)
  React.useEffect(
    () => () => {
      isMounted.current = false
    },
    []
  )
  const omitViewButtonForQueueTypeList = ['Historical Person Match', 'Same Sex Detected']
  const omitViewButtonForQueueNameList = ['CMISX_MISMATCH', 'DEITY', 'HISTORICAL_PERSON']

  const executeButtonCallback = (tableButton) => {
    if (isPending) return
    setIsPending(true)
    // Some buttonCallbacks (e.g. the yad vashem view button) return a non-promise value,
    // so wrap the invocation in a deferred .then() to guarantee a thenable before chaining
    // .then/.catch. Invoking the callback inside .then() (rather than passing its result to
    // Promise.resolve) also converts any synchronous throw into a rejected promise, so the
    // .catch/.finally chain always runs and isPending is reliably reset.
    Promise.resolve()
      .then(() => tableButton.buttonCallback({ message, queuesData }))
      .then((response) => {
        // Only callbacks that resolve to a { messageId, resolution } object should trigger a
        // delete. Some buttons (e.g. Google/Forebears/Yad Vashem view buttons) return a
        // non-promise value such as a URL string or undefined — skip deletion for those.
        if (!response || typeof response !== 'object' || !response.messageId) {
          return undefined
        }
        return deleteMessageById(response.messageId, response.resolution)
          .then(() => {
            queueActions.checkFinalPageAndReload(false)
          })
          .catch((err) => {
            if (err?.response?.status === 404) {
              // Someone (or an earlier click) already resolved this message. Refresh instead of erroring.
              queueActions.checkFinalPageAndReload(false)
              return
            }
            error(t('queues.errorDeletingMessage', 'Error deleting message: {error}', { error: err.message }))
          })
      })
      .catch((err) => {
        error(t('queues.errorProcessingMessage', 'Error processing message: {error}', { error: err.message }))
      })
      .finally(() => {
        if (isMounted.current) setIsPending(false)
      })
  }

  return (
    <DmcGrid columns={[1]} rowGap={5}>
      {queueState.viewButtonCallback !== null &&
        !omitViewButtonForQueueTypeList.some((type) => queueState.queueType?.includes(type)) &&
        !omitViewButtonForQueueNameList.some((name) => queueState.queueName?.includes(name)) && (
          <Button onClick={() => queueState.viewButtonCallback(message, queueActions.getMessageUrl(message))}>
            {t('action.view', 'View')}
          </Button>
        )}
      {queueState.tableButtons
        .filter((tableButton) => !tableButton.isVisible || tableButton.isVisible(message))
        .map((tableButton) => {
          if (tableButton.dialogModalComponent != null) {
            return (
              <React.Fragment key={tableButton.buttonText}>
                {tableButton.dialogModalComponent(message, queueActions.loadPagination)}
              </React.Fragment>
            )
          }
          return (
            <Button
              key={tableButton.buttonText}
              disabled={isPending}
              onClick={() => executeButtonCallback(tableButton)}
            >
              {tableButton.buttonText}
            </Button>
          )
        })}
      {queueState.topButtons.length > 0 && (
        <Checkbox
          name="someName"
          checked={queueActions.isCheckboxSelected(message)}
          onChange={(event) => queueActions.addCheckboxSelection(message, event.target.checked)}
        />
      )}
    </DmcGrid>
  )
}
